/**
 * BenchmarkPage — Real benchmark results for Vettis multi-agent research.
 *
 * Data sourced from 33 evaluation runs (Phase 1 + Phase 2, Feb 2026).
 * All numbers are from actual blind pairwise evaluations judged by
 * Claude Sonnet 4.6 and GPT-4o with position rotation and dual personas.
 */
import { useNavigate } from 'react-router-dom';
import { SEO } from '@/components/SEO';
import {
  Target,
  TrendingUp,
  BookOpen,
  ExternalLink,
  Zap,
  EyeOff,
  RotateCcw,
  Scale,
  Users,
  Snowflake,
  Hexagon,
  ChevronDown,
  Quote,
  ArrowRight,
  Brain,
} from 'lucide-react';
import { useRef, useState } from 'react';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import {
  RadarChart,
  Radar,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
  CartesianGrid,
  ReferenceLine,
} from 'recharts';
import CountUp from 'react-countup';
import { SectionLabel, VerdictFooter, SpotlightCard } from '@/components/verdict';
import { PublicNavbar } from '@/components/layout';
import {
  MODE_WIN_RATES,
  DIMENSION_SCORES,
  getRadarData,
  DIFFICULTY_CARDS,
  GENERAL_DIFFICULTY,
  VETTIS_WINS_QUOTES,
  FRONTIER_WINS_QUOTES,
  TRAJECTORY_DATA,
  METHODOLOGY_ITEMS,
  RESEARCH_PAPERS,
} from '@/data/benchmarkData';

/* ------------------------------------------------------------------ */
/*  Constants & animation helpers                                      */
/* ------------------------------------------------------------------ */

const EASE: [number, number, number, number] = [0.22, 1, 0.36, 1];

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.1, duration: 0.6, ease: EASE },
  }),
};
const stagger = { hidden: {}, visible: { transition: { staggerChildren: 0.12 } } };
const cardPop = {
  hidden: { opacity: 0, y: 30, scale: 0.97 },
  visible: { opacity: 1, y: 0, scale: 1, transition: { duration: 0.5, ease: EASE } },
};

const MODE_TABS = ['strategic', 'financial', 'general'] as const;
const MODE_TAB_LABELS: Record<string, string> = {
  strategic: 'Strategic',
  financial: 'Financial',
  general: 'General',
};
const MODE_TAB_COLORS: Record<string, string> = {
  strategic: '#14b8a6',
  financial: '#22c55e',
  general: '#a78bfa',
};

const ICON_MAP: Record<string, React.ComponentType<{ className?: string }>> = {
  Target,
  TrendingUp,
  BookOpen,
  EyeOff,
  RotateCcw,
  Scale,
  Users,
  Snowflake,
  Hexagon,
  Brain,
};

const CLR = { vettis: '#D4A574', frontier: '#6b7280' };

/* ------------------------------------------------------------------ */
/*  Small helper components                                            */
/* ------------------------------------------------------------------ */

function Stat({ value, suffix, decimals = 0 }: { value: number; suffix: string; decimals?: number }) {
  const ref = useRef<HTMLSpanElement>(null);
  const inView = useInView(ref, { once: true, margin: '-50px' });
  return (
    <span ref={ref}>
      {inView ? <CountUp end={value} duration={2} decimals={decimals} suffix={suffix} /> : <span>0{suffix}</span>}
    </span>
  );
}

function RadarTip({ active, payload }: { active?: boolean; payload?: Array<{ name: string; value: number; color: string }> }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-surface/95 border border-border rounded-lg px-3 py-2 text-xs shadow-xl backdrop-blur-sm space-y-1">
      {payload.map((p) => (
        <div key={p.name} className="flex items-center gap-2">
          <div className="w-2 h-2 rounded-full" style={{ background: p.color }} />
          <span className="text-text-secondary">{p.name}:</span>
          <span className="text-text-primary font-semibold">{p.value}</span>
        </div>
      ))}
    </div>
  );
}

function ProgressBar({ value, color, inView }: { value: number; color: string; inView: boolean }) {
  return (
    <div className="w-full h-1.5 rounded-full bg-surface-light overflow-hidden">
      <div
        className="h-full rounded-full transition-all duration-[1500ms] ease-out"
        style={{
          width: inView ? `${value}%` : '0%',
          background: color,
        }}
      />
    </div>
  );
}

function DeltaPill({ delta }: { delta: number }) {
  const positive = delta >= 0;
  return (
    <span
      className={`inline-flex items-center text-[11px] font-mono px-1.5 py-0.5 rounded ${
        positive ? 'bg-emerald-500/10 text-emerald-400' : 'bg-red-500/10 text-red-400'
      }`}
    >
      {positive ? '+' : ''}
      {delta.toFixed(2)}
    </span>
  );
}

/* ------------------------------------------------------------------ */
/*  Main Page Component                                                */
/* ------------------------------------------------------------------ */

export function BenchmarkPage() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<string>('strategic');
  const [showGeneralDifficulty, setShowGeneralDifficulty] = useState(false);

  // Section refs for scroll-triggered animations
  const heroRef = useRef(null);
  const modeRef = useRef(null);
  const radarRef = useRef(null);
  const diffRef = useRef(null);
  const quotesRef = useRef(null);
  const trajRef = useRef(null);
  const methRef = useRef(null);
  const papersRef = useRef(null);
  const ctaRef = useRef(null);

  const heroInView = useInView(heroRef, { once: true, margin: '-80px' });
  const modeInView = useInView(modeRef, { once: true, margin: '-80px' });
  const radarInView = useInView(radarRef, { once: true, margin: '-80px' });
  const diffInView = useInView(diffRef, { once: true, margin: '-80px' });
  const quotesInView = useInView(quotesRef, { once: true, margin: '-80px' });
  const trajInView = useInView(trajRef, { once: true, margin: '-80px' });
  const methInView = useInView(methRef, { once: true, margin: '-80px' });
  const papersInView = useInView(papersRef, { once: true, margin: '-80px' });
  const ctaInView = useInView(ctaRef, { once: true, margin: '-80px' });

  const radarData = getRadarData(activeTab);
  const dimensionScores = DIMENSION_SCORES[activeTab] ?? DIMENSION_SCORES.strategic;

  return (
    <div className="min-h-screen bg-background text-text-primary verdict-grain verdict-vignette selection:bg-verdict-primary/30">
      <SEO
        title="Benchmarks — Vettis | AI Research Quality Results"
        description="75% win rate on strategic research vs frontier AI models. 33 blind evaluations, 6 dimensions, dual-judge methodology. Real benchmark results."
        path="/benchmark"
      />

      {/* Ambient glow */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-[-20%] left-1/2 -translate-x-1/2 w-[800px] h-[600px] bg-verdict-primary/8 blur-[120px] rounded-full" />
        <div className="absolute bottom-[-30%] right-[-10%] w-[600px] h-[600px] bg-verdict-primary/5 blur-[100px] rounded-full" />
      </div>

      <PublicNavbar currentPage="/benchmark" />

      {/* ================================================================ */}
      {/* SECTION 1: HERO                                                   */}
      {/* ================================================================ */}
      <section className="relative pt-28 sm:pt-32 pb-16 sm:pb-20">
        <div className="w-full px-4 sm:px-6 lg:px-8 xl:px-12 max-w-6xl mx-auto text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
            <SectionLabel className="mb-4 justify-center">BENCHMARK RESULTS</SectionLabel>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight mb-4">
              Tested Against Frontier AI.
              <br />
              <span className="text-verdict-primary">Here's the Verdict.</span>
            </h1>
            <p className="text-text-secondary text-base sm:text-lg max-w-2xl mx-auto mb-3">
              We gave Vettis and top AI models the same research questions, then had independent AI judges blindly pick the better answer.
            </p>
            <p className="text-text-muted text-xs font-mono mb-10">
              33 blind evaluations · 6 quality dimensions · 2 independent AI judges
            </p>
          </motion.div>

          {/* Hero stat cards */}
          <motion.div
            ref={heroRef}
            variants={stagger}
            initial="hidden"
            animate={heroInView ? 'visible' : 'hidden'}
            className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8"
          >
            {[
              { value: 75, suffix: '%', label: 'Win Rate', sublabel: 'Strategic business questions', decimals: 0 },
              { value: 67, suffix: '%', label: 'Win Rate', sublabel: 'Financial analysis questions', decimals: 0 },
              { value: 90, suffix: '%', label: 'Win Rate on Hard Questions', sublabel: 'Complex financial scenarios', decimals: 0 },
            ].map((s) => (
              <motion.div
                key={s.sublabel}
                variants={cardPop}
                className="verdict-card rounded-xl p-5 text-center group hover:border-verdict-primary/30 transition-colors"
              >
                <div className="text-3xl sm:text-4xl font-bold text-verdict-primary mb-1">
                  <Stat value={s.value} suffix={s.suffix} decimals={s.decimals} />
                </div>
                <div className="text-xs sm:text-sm text-text-primary font-medium">{s.label}</div>
                <div className="text-[10px] text-text-muted mt-0.5">{s.sublabel}</div>
              </motion.div>
            ))}
          </motion.div>

          {/* Trust badge row */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.8, duration: 0.5 }}
            className="flex flex-wrap justify-center gap-x-4 gap-y-1 text-[11px] text-text-muted font-mono uppercase tracking-wider"
          >
            {['Dual-judge', 'Position-rotated', 'Frozen snapshots', '6 dimensions'].map((badge) => (
              <span key={badge} className="flex items-center gap-1.5">
                <span className="w-1 h-1 rounded-full bg-verdict-primary/50" />
                {badge}
              </span>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ================================================================ */}
      {/* METHODOLOGY STRIP (condensed, establishes credibility early)      */}
      {/* ================================================================ */}
      <section className="relative py-6 border-t border-border-subtle">
        <div className="w-full px-4 sm:px-6 lg:px-8 xl:px-12 max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1, duration: 0.5 }}
            className="flex flex-wrap justify-center gap-x-5 gap-y-2 text-xs text-text-secondary"
          >
            {[
              { icon: EyeOff, text: 'Blind A/B evaluation' },
              { icon: Scale, text: 'Two AI judge models (Claude + GPT-4o)' },
              { icon: RotateCcw, text: 'Position-rotated to cancel bias' },
              { icon: Snowflake, text: 'Frozen data — no information advantage' },
            ].map((item) => (
              <span key={item.text} className="flex items-center gap-1.5">
                <item.icon className="w-3 h-3 text-verdict-primary/50" />
                {item.text}
              </span>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ================================================================ */}
      {/* SECTION 2: MODE SCORECARD                                        */}
      {/* ================================================================ */}
      <section ref={modeRef} className="relative py-16 sm:py-20 border-t border-border-subtle">
        <div className="w-full px-4 sm:px-6 lg:px-8 xl:px-12 max-w-6xl mx-auto">
          <motion.div initial="hidden" animate={modeInView ? 'visible' : 'hidden'} variants={stagger}>
            <motion.div variants={cardPop} className="text-center mb-12">
              <SectionLabel className="mb-3 justify-center">MODE BREAKDOWN</SectionLabel>
              <h2 className="text-2xl sm:text-3xl font-bold tracking-tight">Performance Across Research Modes</h2>
              <p className="text-text-secondary text-sm mt-2 max-w-xl mx-auto">
                Product comparison: Vettis full pipeline vs. frontier model with a raw prompt.
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {MODE_WIN_RATES.map((m, i) => {
                const Icon = ICON_MAP[m.icon] ?? Target;
                const isGeneral = m.mode === 'general';
                return (
                  <motion.div key={m.mode} custom={i} variants={fadeUp}>
                    <SpotlightCard className="rounded-xl h-full" contentClassName="p-6">
                      <div className="flex items-center gap-3 mb-4">
                        <div
                          className="w-9 h-9 rounded-lg flex items-center justify-center"
                          style={{ background: `${m.accentColor}15`, border: `1px solid ${m.accentColor}30` }}
                        >
                          <Icon className="w-4 h-4" />
                        </div>
                        <span className="text-sm font-medium text-text-primary">{m.label}</span>
                      </div>

                      {isGeneral ? (
                        <>
                          <div className="text-4xl font-bold mb-1" style={{ color: m.accentColor }}>
                            <Stat value={m.displayRate ?? m.archRate} suffix="%" />
                          </div>
                          <p className="text-xs text-text-muted mb-4">
                            Controlled comparison, same data
                          </p>

                          <ProgressBar value={m.displayRate ?? m.archRate} color={m.accentColor} inView={modeInView} />

                          <div className="mt-4 space-y-2">
                            <div className="flex items-center justify-between text-xs">
                              <span className="text-text-muted">Top dimension</span>
                              <span className="font-mono" style={{ color: m.accentColor }}>
                                {m.topDelta} {m.topDimension}
                              </span>
                            </div>
                            <div className="flex items-center justify-between text-xs">
                              <span className="text-text-muted">Hard questions</span>
                              <span className="text-text-primary font-mono">{m.hardRate}</span>
                            </div>
                          </div>

                          {m.note && (
                            <p className="mt-4 text-xs leading-relaxed text-text-secondary">
                              {m.note}
                            </p>
                          )}

                          <p className="mt-2 text-[10px] text-text-muted">
                            Raw product comparison: {m.productRate}%* — judge parsing issues affected scoring
                          </p>
                        </>
                      ) : (
                        <>
                          <div className="text-4xl font-bold mb-1 text-verdict-primary">
                            <Stat value={m.productRate} suffix="%" />
                          </div>
                          <p className="text-xs text-text-muted mb-4">
                            {m.productWins} of {m.productTotal} product comparisons won
                          </p>

                          <ProgressBar value={m.productRate} color={m.accentColor} inView={modeInView} />

                          <div className="mt-4 space-y-2">
                            <div className="flex items-center justify-between text-xs">
                              <span className="text-text-muted">Top dimension</span>
                              <span className="font-mono" style={{ color: m.accentColor }}>
                                {m.topDelta} {m.topDimension}
                              </span>
                            </div>
                            <div className="flex items-center justify-between text-xs">
                              <span className="text-text-muted">Hard questions</span>
                              <span className="text-text-primary font-mono">{m.hardRate}</span>
                            </div>
                            <div className="flex items-center justify-between text-xs">
                              <span className="text-text-muted">Medium questions</span>
                              <span className="text-text-primary font-mono">{m.mediumRate}</span>
                            </div>
                          </div>

                          {m.note && (
                            <p className="mt-4 text-xs leading-relaxed text-text-secondary">
                              {m.note}
                            </p>
                          )}
                        </>
                      )}
                    </SpotlightCard>
                  </motion.div>
                );
              })}
            </div>
          </motion.div>
        </div>
      </section>

      {/* ================================================================ */}
      {/* SECTION 3: DIMENSION RADAR                                       */}
      {/* ================================================================ */}
      <section ref={radarRef} className="relative py-16 sm:py-20 border-t border-border-subtle">
        <div className="w-full px-4 sm:px-6 lg:px-8 xl:px-12 max-w-6xl mx-auto">
          <motion.div initial="hidden" animate={radarInView ? 'visible' : 'hidden'} variants={stagger}>
            <motion.div variants={cardPop} className="text-center mb-10">
              <SectionLabel className="mb-3 justify-center">DIMENSION ANALYSIS</SectionLabel>
              <h2 className="text-2xl sm:text-3xl font-bold tracking-tight">Six Dimensions. No Cherry-Picking.</h2>
              <p className="text-text-secondary text-sm mt-2 max-w-xl mx-auto">
                Every comparison is scored across accuracy, depth, actionability, risk awareness, calibration, and clarity.
              </p>
            </motion.div>

            {/* Tab selector */}
            <motion.div variants={cardPop} className="flex justify-center gap-2 mb-8">
              {MODE_TABS.map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`px-4 py-2 rounded-lg text-xs font-medium transition-all ${
                    activeTab === tab
                      ? 'text-text-primary'
                      : 'text-text-muted hover:text-text-primary bg-transparent'
                  }`}
                  style={
                    activeTab === tab
                      ? { background: `${MODE_TAB_COLORS[tab]}20`, border: `1px solid ${MODE_TAB_COLORS[tab]}40` }
                      : { border: '1px solid transparent' }
                  }
                >
                  {MODE_TAB_LABELS[tab]}
                </button>
              ))}
            </motion.div>

            {/* Radar + dimension list */}
            <AnimatePresence mode="wait">
              <motion.div
                key={activeTab}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.3, ease: EASE }}
                className="flex flex-col lg:flex-row gap-6"
              >
                {/* Radar chart */}
                <div className="verdict-card rounded-2xl p-6 sm:p-8 lg:w-[55%]">
                  <div className="flex flex-wrap items-center justify-center gap-6 mb-4 text-xs">
                    <span className="flex items-center gap-2">
                      <span className="w-3 h-3 rounded-full" style={{ background: CLR.vettis }} /> Vettis
                    </span>
                    <span className="flex items-center gap-2">
                      <span className="w-3 h-3 rounded-full" style={{ background: CLR.frontier }} /> Frontier
                    </span>
                  </div>
                  <ResponsiveContainer width="100%" height={340}>
                    <RadarChart data={radarData} cx="50%" cy="50%" outerRadius="72%">
                      <PolarGrid stroke="rgba(255,255,255,0.08)" />
                      <PolarAngleAxis dataKey="dimension" tick={{ fill: 'rgb(var(--color-text-secondary))', fontSize: 11 }} />
                      <PolarRadiusAxis angle={90} domain={[3.0, 5.0]} tick={false} axisLine={false} />
                      <Radar name="Frontier" dataKey="frontier" stroke={CLR.frontier} fill={CLR.frontier} fillOpacity={0.12} strokeWidth={1.5} />
                      <Radar name="Vettis" dataKey="vettis" stroke={CLR.vettis} fill={CLR.vettis} fillOpacity={0.22} strokeWidth={2} />
                      <Tooltip content={<RadarTip />} />
                    </RadarChart>
                  </ResponsiveContainer>
                </div>

                {/* Dimension breakdown list */}
                <div className="lg:w-[45%] space-y-3">
                  {dimensionScores.map((d, i) => (
                    <motion.div
                      key={d.dimension}
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: i * 0.06, duration: 0.4, ease: EASE }}
                      className="verdict-card rounded-lg px-4 py-3 flex items-center justify-between gap-3"
                    >
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-text-primary truncate">{d.dimension}</div>
                        <div className="flex items-center gap-3 mt-0.5 text-xs text-text-muted">
                          <span>
                            Vettis <span className="text-text-primary font-mono">{d.vettis.toFixed(2)}</span>
                          </span>
                          <span>
                            Frontier <span className="text-text-primary font-mono">{d.frontier.toFixed(2)}</span>
                          </span>
                        </div>
                      </div>
                      <DeltaPill delta={d.delta} />
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            </AnimatePresence>
          </motion.div>
        </div>
      </section>

      {/* ================================================================ */}
      {/* SECTION 4: DIFFICULTY SCALING                                     */}
      {/* ================================================================ */}
      <section ref={diffRef} className="relative py-16 sm:py-20 border-t border-border-subtle">
        <div className="w-full px-4 sm:px-6 lg:px-8 xl:px-12 max-w-6xl mx-auto">
          <motion.div initial="hidden" animate={diffInView ? 'visible' : 'hidden'} variants={stagger}>
            <motion.div variants={cardPop} className="text-center mb-10">
              <SectionLabel className="mb-3 justify-center">DIFFICULTY SCALING</SectionLabel>
              <h2 className="text-2xl sm:text-3xl font-bold tracking-tight">Harder Questions, Bigger Edge</h2>
              <p className="text-text-secondary text-sm mt-2 max-w-xl mx-auto">
                Where single models struggle with complexity, multi-agent debate shows its largest advantage.
              </p>
            </motion.div>

            <motion.div variants={cardPop} className="grid grid-cols-1 sm:grid-cols-2 gap-5 mb-6">
              {DIFFICULTY_CARDS.map((d) => (
                <div key={d.difficulty} className="verdict-card rounded-2xl p-6">
                  <div className="flex items-center justify-between mb-1">
                    <h3 className="text-lg font-bold text-text-primary">{d.difficulty} Questions</h3>
                    {d.difficulty === 'Hard' && (
                      <span className="text-[10px] font-mono uppercase tracking-wider text-verdict-primary bg-verdict-primary/10 border border-verdict-primary/20 rounded-full px-2 py-0.5">
                        Biggest Edge
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-text-muted mb-5">{d.description}</p>

                  <div className="space-y-4">
                    {[
                      { label: 'Strategic', value: d.strategic, color: '#14b8a6' },
                      { label: 'Financial', value: d.financial, color: '#22c55e' },
                      ...(d.general !== null ? [{ label: 'General', value: d.general, color: '#a78bfa' }] : []),
                    ].map((row) => (
                      <div key={row.label}>
                        <div className="flex items-center justify-between text-xs mb-1.5">
                          <span className="text-text-secondary">{row.label}</span>
                          <span className="font-mono font-semibold" style={{ color: row.color }}>{row.value}%</span>
                        </div>
                        <div className="w-full h-2 rounded-full bg-surface-light overflow-hidden">
                          <motion.div
                            className="h-full rounded-full"
                            style={{ background: row.color }}
                            initial={{ width: 0 }}
                            animate={{ width: diffInView ? `${row.value}%` : '0%' }}
                            transition={{ duration: 1.2, ease: EASE }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </motion.div>

            {/* Callout */}
            <motion.div variants={cardPop} className="verdict-card rounded-xl p-5 border-l-2 border-verdict-primary/50 flex items-start gap-3 mb-4">
              <Zap className="w-5 h-5 text-verdict-primary flex-shrink-0 mt-0.5" />
              <p className="text-sm text-text-primary">
                On hard financial questions: <span className="text-verdict-primary font-semibold">90% win rate.</span>{' '}
                Multi-agent debate's largest advantage is where analysis is most complex.
              </p>
            </motion.div>

            {/* Expandable general difficulty */}
            <motion.div variants={cardPop}>
              <button
                onClick={() => setShowGeneralDifficulty(!showGeneralDifficulty)}
                className="flex items-center gap-2 text-xs text-text-muted hover:text-text-secondary transition-colors"
              >
                <ChevronDown className={`w-3.5 h-3.5 transition-transform ${showGeneralDifficulty ? 'rotate-180' : ''}`} />
                General mode difficulty breakdown
              </button>
              <AnimatePresence>
                {showGeneralDifficulty && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="overflow-hidden"
                  >
                    <div className="mt-3 flex gap-4 text-xs text-text-muted">
                      {GENERAL_DIFFICULTY.map((d) => (
                        <span key={d.difficulty} className="flex items-center gap-1.5">
                          {d.difficulty}: <span className="text-text-primary font-mono">{d.rate}%</span>
                        </span>
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ================================================================ */}
      {/* SECTION 5: JUDGE QUOTES                                          */}
      {/* ================================================================ */}
      <section ref={quotesRef} className="relative py-16 sm:py-20 border-t border-border-subtle">
        <div className="w-full px-4 sm:px-6 lg:px-8 xl:px-12 max-w-6xl mx-auto">
          <motion.div initial="hidden" animate={quotesInView ? 'visible' : 'hidden'} variants={stagger}>
            <motion.div variants={cardPop} className="text-center mb-12">
              <SectionLabel className="mb-3 justify-center">JUDGE EVALUATIONS</SectionLabel>
              <h2 className="text-2xl sm:text-3xl font-bold tracking-tight">What the Evaluators Said</h2>
              <p className="text-text-secondary text-sm mt-2 max-w-xl mx-auto">
                Real feedback from blind evaluations. No cherry-picking — we show both sides.
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Vettis wins column */}
              <div className="space-y-4">
                <h3 className="text-xs font-mono uppercase tracking-wider text-verdict-primary/70 mb-4">When Vettis Wins</h3>
                {VETTIS_WINS_QUOTES.map((q, i) => (
                  <motion.div
                    key={i}
                    custom={i}
                    variants={fadeUp}
                    className="verdict-card rounded-xl p-5 border-l-2 border-verdict-primary/40"
                  >
                    <Quote className="w-4 h-4 text-verdict-primary/40 mb-2" />
                    <p className="text-sm text-text-primary italic leading-relaxed mb-3">"{q.quote}"</p>
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] text-text-muted">{q.context}</span>
                      <span className="text-[10px] font-mono text-verdict-primary/50 bg-verdict-primary/10 rounded-full px-2 py-0.5">
                        {q.dimension}
                      </span>
                    </div>
                  </motion.div>
                ))}
              </div>

              {/* Frontier wins column */}
              <div className="space-y-4">
                <h3 className="text-xs font-mono uppercase tracking-wider text-text-muted mb-4">When Frontier Wins</h3>
                {FRONTIER_WINS_QUOTES.map((q, i) => (
                  <motion.div
                    key={i}
                    custom={i}
                    variants={fadeUp}
                    className="verdict-card rounded-xl p-5 border-l-2 border-border-light/40"
                  >
                    <Quote className="w-4 h-4 text-text-muted mb-2" />
                    <p className="text-sm text-text-secondary italic leading-relaxed mb-3">"{q.quote}"</p>
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] text-text-muted">{q.context}</span>
                      <span className="text-[10px] font-mono text-text-muted bg-surface-light rounded-full px-2 py-0.5">
                        {q.dimension}
                      </span>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ================================================================ */}
      {/* SECTION 6: DEVELOPMENT TRAJECTORY                                */}
      {/* ================================================================ */}
      <section ref={trajRef} className="relative py-16 sm:py-20 border-t border-border-subtle">
        <div className="w-full px-4 sm:px-6 lg:px-8 xl:px-12 max-w-6xl mx-auto">
          <motion.div initial="hidden" animate={trajInView ? 'visible' : 'hidden'} variants={stagger}>
            <motion.div variants={cardPop} className="text-center mb-10">
              <SectionLabel className="mb-3 justify-center">ITERATION HISTORY</SectionLabel>
              <h2 className="text-2xl sm:text-3xl font-bold tracking-tight">
                From 0% to <span className="text-verdict-primary">75%</span>
              </h2>
              <p className="text-text-secondary text-sm mt-2 max-w-xl mx-auto">
                Same test cases throughout — only the architecture improved. Each step shows what changed and the result.
              </p>
            </motion.div>

            <motion.div variants={cardPop} className="verdict-card rounded-2xl p-6 sm:p-8">
              <ResponsiveContainer width="100%" height={320}>
                <LineChart data={TRAJECTORY_DATA} margin={{ left: 0, right: 70, top: 10, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                  <XAxis dataKey="label" tick={{ fill: 'rgb(var(--color-text-secondary))', fontSize: 10 }} axisLine={false} tickLine={false} />
                  <YAxis domain={[0, 100]} tick={{ fill: 'rgb(var(--color-text-muted))', fontSize: 10 }} axisLine={false} tickLine={false} tickFormatter={(v: number) => `${v}%`} />
                  <ReferenceLine y={50} stroke="rgba(255,255,255,0.12)" strokeDasharray="3 3" label={({ viewBox }: { viewBox?: { x?: number; y?: number; width?: number } }) => {
                    const x = (viewBox?.x ?? 0) + (viewBox?.width ?? 0) + 6;
                    const y = (viewBox?.y ?? 0) + 3;
                    return (
                      <text x={x} y={y} fill="rgb(var(--color-text-muted))" fontSize={9} textAnchor="start">
                        50%
                      </text>
                    );
                  }} />
                  <Tooltip
                    contentStyle={{ background: 'rgb(var(--color-surface) / 0.95)', border: '1px solid rgb(var(--color-border) / 0.5)', borderRadius: 8, fontSize: 12 }}
                    labelStyle={{ color: 'rgb(var(--color-text-secondary))' }}
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    formatter={((value: any, name: any) => [`${value ?? 0}%`, name === 'strategic' ? 'Strategic' : name === 'financial' ? 'Financial' : 'General']) as any}
                  />
                  <Line
                    type="monotone"
                    dataKey="strategic"
                    stroke="#D4A574"
                    strokeWidth={2.5}
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    dot={({ cx, cy, index, value }: any) => {
                      if (!cx || !cy) return <circle r={0} />;
                      const isLast = index === TRAJECTORY_DATA.length - 1;
                      return (
                        <g key={`s-${cx}-${cy}`}>
                          <circle cx={cx} cy={cy} r={isLast ? 5 : 3} fill="#D4A574" strokeWidth={0} />
                          {isLast && <circle cx={cx} cy={cy} r={9} fill="none" stroke="#D4A574" strokeWidth={1} opacity={0.2} />}
                          {isLast && <text x={cx + 16} y={cy + 4} fill="#D4A574" fontSize={11} fontWeight={600}>{value}%</text>}
                        </g>
                      );
                    }}
                    activeDot={{ r: 6, fill: '#D4A574' }}
                    animationDuration={2000}
                  />
                  <Line
                    type="monotone"
                    dataKey="financial"
                    stroke="#22c55e"
                    strokeWidth={2}
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    dot={({ cx, cy, index, value }: any) => {
                      if (!cx || !cy) return <circle r={0} />;
                      const isLast = index === TRAJECTORY_DATA.length - 1;
                      return (
                        <g key={`f-${cx}-${cy}`}>
                          <circle cx={cx} cy={cy} r={isLast ? 5 : 3} fill="#22c55e" strokeWidth={0} />
                          {isLast && <circle cx={cx} cy={cy} r={9} fill="none" stroke="#22c55e" strokeWidth={1} opacity={0.2} />}
                          {isLast && <text x={cx + 16} y={cy + 4} fill="#22c55e" fontSize={11} fontWeight={600}>{value}%</text>}
                        </g>
                      );
                    }}
                    activeDot={{ r: 5, fill: '#22c55e' }}
                    animationDuration={2000}
                  />
                  <Line
                    type="monotone"
                    dataKey="general"
                    stroke="#a78bfa"
                    strokeWidth={2}
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    dot={({ cx, cy, index, value }: any) => {
                      if (!cx || !cy) return <circle r={0} />;
                      const isLast = index === TRAJECTORY_DATA.length - 1;
                      return (
                        <g key={`g-${cx}-${cy}`}>
                          <circle cx={cx} cy={cy} r={isLast ? 5 : 3} fill="#a78bfa" strokeWidth={0} />
                          {isLast && <circle cx={cx} cy={cy} r={9} fill="none" stroke="#a78bfa" strokeWidth={1} opacity={0.2} />}
                          {isLast && <text x={cx + 16} y={cy + 4} fill="#a78bfa" fontSize={11} fontWeight={600}>{value}%</text>}
                        </g>
                      );
                    }}
                    activeDot={{ r: 5, fill: '#a78bfa' }}
                    animationDuration={2000}
                  />
                </LineChart>
              </ResponsiveContainer>
              <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 mt-4 text-xs text-text-secondary">
                <span className="flex items-center gap-2">
                  <span className="w-3 h-0.5 bg-[#D4A574] rounded" /> Strategic <span className="text-text-muted">(0% → 75%)</span>
                </span>
                <span className="flex items-center gap-2">
                  <span className="w-3 h-0.5 bg-[#22c55e] rounded" /> Financial <span className="text-text-muted">(0% → 67%)</span>
                </span>
                <span className="flex items-center gap-2">
                  <span className="w-3 h-0.5 bg-[#a78bfa] rounded" /> General <span className="text-text-muted">(0% → 61%)</span>
                </span>
              </div>

              {/* Key milestones as timeline strip */}
              <div className="mt-6 flex flex-col sm:flex-row">
                {TRAJECTORY_DATA.filter(d => d.annotation).map((m, i, arr) => (
                  <div key={m.label} className={`flex-1 ${i < arr.length - 1 ? 'sm:border-r border-border-subtle' : ''} ${i > 0 ? 'border-t sm:border-t-0 border-border-subtle' : ''}`}>
                    <div className="px-3 py-2">
                      <div className="text-[10px] font-mono text-verdict-primary/70 mb-0.5">{m.label}</div>
                      <div className="text-xs text-text-secondary">{m.annotation}</div>
                      <div className="text-[10px] font-mono text-text-muted mt-0.5">
                        <span style={{ color: '#D4A574' }}>S: {m.strategic}%</span>
                        {' / '}
                        <span style={{ color: '#22c55e' }}>F: {m.financial}%</span>
                        {' / '}
                        <span style={{ color: '#a78bfa' }}>G: {m.general}%</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ================================================================ */}
      {/* SECTION 7: METHODOLOGY                                           */}
      {/* ================================================================ */}
      <section ref={methRef} className="relative py-16 sm:py-20 border-t border-border-subtle">
        <div className="w-full px-4 sm:px-6 lg:px-8 xl:px-12 max-w-6xl mx-auto">
          <motion.div initial="hidden" animate={methInView ? 'visible' : 'hidden'} variants={stagger}>
            <motion.div variants={cardPop} className="text-center mb-12">
              <SectionLabel className="mb-3 justify-center">METHODOLOGY</SectionLabel>
              <h2 className="text-2xl sm:text-3xl font-bold tracking-tight">Rigorous, Reproducible, Open</h2>
              <p className="text-text-secondary text-sm mt-2 max-w-xl mx-auto">
                Every design choice in our benchmark exists to eliminate bias and ensure the numbers mean something.
              </p>
            </motion.div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {METHODOLOGY_ITEMS.map((item, i) => {
                const Icon = ICON_MAP[item.icon] ?? Hexagon;
                return (
                  <motion.div
                    key={item.title}
                    custom={i}
                    variants={fadeUp}
                    className="verdict-card rounded-xl p-5 group hover:border-verdict-primary/30 transition-colors"
                  >
                    <div className="w-9 h-9 mb-3 rounded-lg bg-verdict-primary/10 border border-verdict-primary/20 flex items-center justify-center group-hover:bg-verdict-primary/20 transition-colors">
                      <Icon className="w-4.5 h-4.5 text-verdict-primary" />
                    </div>
                    <h3 className="text-sm font-semibold text-text-primary mb-1">{item.title}</h3>
                    <p className="text-xs text-text-secondary leading-relaxed">{item.description}</p>
                  </motion.div>
                );
              })}
            </div>
          </motion.div>
        </div>
      </section>

      {/* ================================================================ */}
      {/* SECTION 8: RESEARCH PAPERS                                       */}
      {/* ================================================================ */}
      <section ref={papersRef} className="relative py-16 sm:py-20 border-t border-border-subtle">
        <div className="w-full px-4 sm:px-6 lg:px-8 xl:px-12 max-w-6xl mx-auto">
          <motion.div initial="hidden" animate={papersInView ? 'visible' : 'hidden'} variants={stagger}>
            <motion.div variants={cardPop} className="text-center mb-12">
              <SectionLabel className="mb-3 justify-center">RESEARCH FOUNDATION</SectionLabel>
              <h2 className="text-2xl sm:text-3xl font-bold tracking-tight">Backed by Peer-Reviewed Science</h2>
              <p className="text-text-secondary text-sm mt-2 max-w-xl mx-auto">
                Our multi-agent debate architecture is grounded in published research from MIT, Tsinghua, IJCAI, and other leading institutions.
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {RESEARCH_PAPERS.map((paper) => (
                <motion.a
                  key={paper.title}
                  href={paper.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  variants={cardPop}
                  className="verdict-card rounded-xl p-5 sm:p-6 group hover:border-verdict-primary/30 transition-colors block"
                >
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <span className="text-[10px] font-mono uppercase tracking-wider text-verdict-primary/70 bg-verdict-primary/10 border border-verdict-primary/20 rounded-full px-2 py-0.5">
                      {paper.tag}
                    </span>
                    <ExternalLink className="w-3.5 h-3.5 text-text-muted group-hover:text-verdict-primary transition-colors flex-shrink-0 mt-0.5" />
                  </div>
                  <h3 className="text-sm font-semibold text-text-primary mb-2 leading-snug group-hover:text-verdict-primary/90 transition-colors">
                    {paper.title}
                  </h3>
                  <p className="text-xs text-text-muted mb-3">
                    {paper.authors} &middot; {paper.venue} {paper.year}
                  </p>
                  <p className="text-xs text-text-secondary leading-relaxed">{paper.relevance}</p>
                </motion.a>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* ================================================================ */}
      {/* SECTION 9: FINAL CTA                                             */}
      {/* ================================================================ */}
      <section ref={ctaRef} className="relative py-20 sm:py-28 border-t border-border-subtle">
        <div className="w-full px-4 sm:px-6 lg:px-8 xl:px-12 max-w-6xl mx-auto text-center">
          <motion.div initial="hidden" animate={ctaInView ? 'visible' : 'hidden'} variants={stagger}>
            <motion.div variants={cardPop}>
              <h2 className="text-2xl sm:text-3xl font-bold tracking-tight mb-3">See the Difference on Your Question</h2>
              <p className="text-text-secondary text-sm mb-8 max-w-md mx-auto">
                Try Vettis free. No credit card required.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <button
                  onClick={() => navigate('/dashboard')}
                  className="inline-flex items-center gap-2 px-6 py-2.5 text-sm font-medium bg-verdict-primary text-black rounded-lg hover:bg-verdict-primary/90 transition-colors shadow-gold-md"
                >
                  <Zap className="w-4 h-4" /> Start Free Research
                </button>
                <button
                  onClick={() => navigate('/pricing')}
                  className="inline-flex items-center gap-2 px-6 py-2.5 text-sm font-medium text-text-primary border border-border-subtle rounded-lg hover:border-verdict-primary/30 hover:text-text-primary transition-colors"
                >
                  View Pricing <ArrowRight className="w-3.5 h-3.5" />
                </button>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      <VerdictFooter />
    </div>
  );
}
